/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React, { useState, useEffect } from 'react';
import { logger } from '@musedrops/shared';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { AudioMixConfig } from '../components/AudioMixConfig';
import { SchedulingConfig } from '../components/SchedulingConfig';
import { configApi, ConfigItem, AppConfig } from '../../../services/configApi';
import { LAYOUT } from '../../../theme';

interface ContentGenSettingsState {
  items: ConfigItem[];
  config: AppConfig;
  originalConfig: AppConfig;
  loading: boolean;
  saving: boolean;
  error: string | null;
  success: string | null;
  hasChanges: boolean;
}

export const ContentGenSettings: React.FC = () => {
  const [state, setState] = useState<ContentGenSettingsState>({
    items: [],
    config: {
      max_concurrent_jobs: 0,
      log_retention_days: 0,
      default_publish_weekdays: null,
    },
    originalConfig: {
      max_concurrent_jobs: 0,
      log_retention_days: 0,
      default_publish_weekdays: null,
    },
    loading: true,
    saving: false,
    error: null,
    success: null,
    hasChanges: false,
  });

  // Load initial configuration
  useEffect(() => {
    loadConfiguration();
  }, []);

  // Warn about unsaved changes when leaving
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (state.hasChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [state.hasChanges]);

  const loadConfiguration = async () => {
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      const { items, config } = await configApi.getAppConfig();

      setState(prev => ({
        ...prev,
        items,
        config,
        originalConfig: { ...config },
        loading: false,
        hasChanges: false,
      }));
    } catch (error) {
      logger.error('Failed to load configuration:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to load configuration',
      }));
    }
  };

  const handleConfigChange = (
    key: keyof AppConfig,
    value: number | boolean
  ) => {
    setState(prev => {
      const newConfig = { ...prev.config, [key]: value };
      const hasChanges =
        JSON.stringify(newConfig) !== JSON.stringify(prev.originalConfig);

      return {
        ...prev,
        config: newConfig,
        hasChanges,
        error: null,
      };
    });
  };

  const handleSave = async () => {
    try {
      setState(prev => ({ ...prev, saving: true, error: null }));

      // Find changed values
      const updates = Object.entries(state.config)
        .filter(
          ([key, value]) =>
            state.originalConfig[key as keyof typeof state.originalConfig] !==
            value
        )
        .map(([key, value]) => ({ key, value }));

      if (updates.length === 0) {
        setState(prev => ({ ...prev, saving: false }));
        return;
      }

      // Update configurations
      await configApi.updateMultipleConfigurations(updates);

      // Reload configuration from server to get fresh values
      await loadConfiguration();

      setState(prev => ({
        ...prev,
        saving: false,
        success: 'Configuration saved successfully!',
      }));
    } catch (error) {
      setState(prev => ({
        ...prev,
        saving: false,
        error:
          error instanceof Error
            ? error.message
            : 'Failed to save configuration',
      }));
    }
  };

  const handleReset = (key: string) => {
    // Reset field to original value from database (like discard changes for single field)
    const originalValue = state.originalConfig[key as keyof AppConfig];
    if (originalValue !== undefined) {
      handleConfigChange(key as keyof AppConfig, originalValue);
    }
  };

  const handleDiscardChanges = () => {
    setState(prev => ({
      ...prev,
      config: { ...prev.originalConfig },
      hasChanges: false,
    }));
  };

  const closeSnackbar = () => {
    setState(prev => ({ ...prev, error: null, success: null }));
  };

  if (state.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={400}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ py: 3, maxWidth: LAYOUT.mediumContentWidth, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {/* Scheduling Configuration */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Story Scheduling
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Default publishing schedule for stories (can be overridden per
              show)
            </Typography>

            <SchedulingConfig
              config={state.config}
              items={state.items}
              disabled={state.saving}
            />
          </CardContent>
        </Card>

        {/* Audio Mix Configuration */}
        <Card>
          <CardContent>
            <AudioMixConfig />
          </CardContent>
        </Card>
      </Box>

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!state.success}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert
          onClose={closeSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          {state.success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!state.error}
        autoHideDuration={6000}
        onClose={closeSnackbar}
      >
        <Alert onClose={closeSnackbar} severity="error" sx={{ width: '100%' }}>
          {state.error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
